import {
  FULL_LENGTH_MONTH_FORMAT,
  FULL_LENGTH_MONTH_YEAR_FORMAT,
  ISO_DATE_FORMAT_STRING,
} from '@/utils/dayjs';
import dayjs, { QUnitType } from 'dayjs';
import quarterOfYear from 'dayjs/plugin/quarterOfYear';
import { EARLIEST_SELECTABLE_MONTH } from '../constants';
import { PaymentPeriodDateRange, ReportingPeriod } from '../types';

/**
 * https://github.com/iamkun/dayjs/issues/655
 * Issue regarding plugins in one place will affect all instances of dayjs.
 * However, it seems like each story utilizes a different instance of dayjs when loaded in Storybook.
 */
dayjs.extend(quarterOfYear);

interface ISelectablePaymentPeriod {
  value: string; // Long form ISO string including timezone of the start of the payment period
  label: string; // e.g. January 2024
}

/**
 * @param reportingPeriodType A value of either "MONTHLY" or "QUARTERLY" for
 * the format of reportable periods for a given jurisdiction.
 * @returns Either "M" or "Q" ("month" and "quarters" respectively) to feed to other
 * DayJS based functions that need to know the length of duration to operate on.
 */
function paymentPeriodUnitType(
  reportingPeriodType: ReportingPeriod
): 'M' | 'Q' {
  return reportingPeriodType === ReportingPeriod.MONTHLY ? 'M' : 'Q';
}

/**
 * @param reportingPeriodType A value of either "MONTHLY" or "QUARTERLY" for
 * the format of reportable periods for a given jurisdiction.
 * @param isoDate ISO 8601 date string
 * @returns A user friendly string that displays the month/quarter and year of the date string provided.
 * e.g. January 2024 or January - March 2024
 */
export function paymentPeriodLabelFromISODate(
  reportingPeriodType: ReportingPeriod,
  isoDate: string
) {
  const isSingleMonthStyle = reportingPeriodType === ReportingPeriod.MONTHLY;
  const day = dayjs(isoDate);

  if (isSingleMonthStyle) {
    return day.format(FULL_LENGTH_MONTH_YEAR_FORMAT);
  }

  const unit = paymentPeriodUnitType(reportingPeriodType);
  const startingMonth = day.startOf(unit).format(FULL_LENGTH_MONTH_FORMAT);
  const endingMonthYear = day.endOf(unit).format(FULL_LENGTH_MONTH_YEAR_FORMAT);

  return `${startingMonth} - ${endingMonthYear}`;
}

/**
 * @param reportingPeriodType A value of either "MONTHLY" or "QUARTERLY" for
 * the format of reportable periods for a given jurisdiction.
 * @param cutOffPaymentPeriod ISO 8601 date string for the last selectable payment period.
 * This date string does not have to be exact as it will be parsed to determine the payment period that
 * contains this date and from that the ending date will be found.
 * @returns A list of payment periods starting from Jan 2017 to the current ongoing payment period.
 */
export function listSelectablePaymentPeriods(
  reportingPeriodType: ReportingPeriod,
  cutOffPaymentPeriod: string
): ISelectablePaymentPeriod[] {
  const selectablePaymentPaymentPeriods: ISelectablePaymentPeriod[] = [];
  const periodUnit = paymentPeriodUnitType(reportingPeriodType);
  const earliestSelectableMonth = EARLIEST_SELECTABLE_MONTH.startOf(periodUnit);

  for (
    let startOfCurrentPeriod = dayjs(cutOffPaymentPeriod).startOf(periodUnit);
    startOfCurrentPeriod.isSame(earliestSelectableMonth) ||
    startOfCurrentPeriod.isAfter(earliestSelectableMonth);
    startOfCurrentPeriod = startOfCurrentPeriod.subtract(1, periodUnit)
  ) {
    const isoDate = startOfCurrentPeriod.format(ISO_DATE_FORMAT_STRING);
    const selectablePaymentPeriod: ISelectablePaymentPeriod = {
      value: isoDate,
      label: paymentPeriodLabelFromISODate(reportingPeriodType, isoDate),
    };

    selectablePaymentPaymentPeriods.push(selectablePaymentPeriod);
  }

  return selectablePaymentPaymentPeriods;
}

/**
 * @param reportingPeriodType A value of either "MONTHLY" or "QUARTERLY" for
 * the format of reportable periods for a given jurisdiction.
 * @param isoDate ISO 8601 date string
 * @returns An object containing the start and end date of month or quarter that
 * contains the ISO 8601 date string, if provided, or of the current date, when
 * a ISO 8601 date string is not provided.
 */
export function paymentPeriodDateRangeFromISODate(
  reportingPeriodType: ReportingPeriod,
  isoDate?: string
): PaymentPeriodDateRange {
  const periodUnit: QUnitType = paymentPeriodUnitType(reportingPeriodType);
  const day = dayjs(isoDate);

  return {
    startDate: day.startOf(periodUnit).format(ISO_DATE_FORMAT_STRING),
    endDate: day.endOf(periodUnit).format(ISO_DATE_FORMAT_STRING),
  };
}

/**
 * @param reportingPeriodType A value of either "MONTHLY" or "QUARTERLY" for
 * the format of reportable periods for a given jurisdiction.
 * @returns An ISO 8601 date string of the most recent completed payment period.
 */
export function mostRecentCompletedPaymentPeriod(
  reportingPeriodType: ReportingPeriod
) {
  return dayjs()
    .subtract(1, paymentPeriodUnitType(reportingPeriodType))
    .format(ISO_DATE_FORMAT_STRING);
}
